/**
 * BuilderPreview — Live preview canvas for the custom visualization builder.
 * Renders the current preset definition in real-time using a CustomVisualization instance.
 */

import { useEffect, useRef } from 'react';
import { CustomVisualization } from '../CustomVisualization';
import { CustomVisualizationPreset } from '../types';
import { FrameData } from '../../types';

interface BuilderPreviewProps {
  preset: CustomVisualizationPreset;
  /** Version counter — increment to force re-init (e.g., after adding/removing layers) */
  version: number;
}

/** Generate a silent frame with gentle animation for preview without audio */
function makeSilentFrame(elapsed: number): FrameData {
  const freq = new Uint8Array(1024);
  const time = new Uint8Array(1024).fill(128);

  // Simulate some gentle frequency activity for visual preview
  const t = elapsed / 1000;
  for (let i = 0; i < 200; i++) {
    freq[i] = Math.floor(
      (Math.sin(t * 2 + i * 0.05) * 0.3 + 0.4) * 128 * Math.exp(-i * 0.01)
    );
  }
  // Simulate mild waveform
  for (let i = 0; i < 1024; i++) {
    time[i] = 128 + Math.floor(Math.sin(t * 3 + i * 0.02) * 30);
  }

  const bassEnergy = 0.3 + Math.sin(t * 1.5) * 0.15;
  const midEnergy = 0.25 + Math.sin(t * 2.1) * 0.1;
  const trebleEnergy = 0.15 + Math.sin(t * 3.3) * 0.08;
  const isBeat = Math.sin(t * 3) > 0.95;

  return {
    frequencyData: freq,
    timeDomainData: time,
    bassEnergy,
    midEnergy,
    trebleEnergy,
    overallEnergy: (bassEnergy + midEnergy + trebleEnergy) / 3,
    isBeat,
    beatIntensity: isBeat ? 0.7 : 0,
    bpm: 120,
    timestamp: performance.now(),
    deltaTime: 16.67,
    elapsed,
  };
}

export default function BuilderPreview({ preset, version }: BuilderPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const vizRef = useRef<CustomVisualization | null>(null);
  const animRef = useRef<number | null>(null);
  const startTimeRef = useRef(performance.now());

  // Re-create the visualization when the preset structure changes
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;

    // Size the canvas
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Destroy previous
    vizRef.current?.destroy();

    // Create fresh viz from current preset
    const viz = new CustomVisualization(preset);
    viz.init(canvas);
    viz.resize(canvas.width, canvas.height);
    vizRef.current = viz;
    startTimeRef.current = performance.now();

    // Render loop
    const loop = () => {
      animRef.current = requestAnimationFrame(loop);
      const elapsed = performance.now() - startTimeRef.current;
      const frame = makeSilentFrame(elapsed);
      viz.render(frame);
    };
    loop();

    return () => {
      if (animRef.current !== null) cancelAnimationFrame(animRef.current);
      viz.destroy();
      vizRef.current = null;
    };
  }, [version]); // eslint-disable-line react-hooks/exhaustive-deps
  // Note: We intentionally re-read `preset` inside the effect via closure.
  // The `version` prop is bumped for structural changes; for setting tweaks,
  // the viz reads from the mutable preset reference each frame.

  // Handle resize
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      canvas.width = width;
      canvas.height = height;
      vizRef.current?.resize(width, height);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="w-full h-full bg-black rounded-lg overflow-hidden">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  );
}
